import { useEffect, useMemo, useState } from "react";
import { LayoutGrid, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { DashboardRenderer } from "./Widgets";

function applyLayout(widgets, layout) {
  if (!layout) return widgets;
  const pos = Object.fromEntries((layout.order || []).map((id, i) => [id, i]));
  return [...widgets]
    .map((w, i) => ({ ...w, width: layout.widths?.[w.id] || w.width, _i: pos[w.id] ?? 1000 + i }))
    .sort((a, b) => a._i - b._i);
}

export function PersonalDashboard({ dashboard, data, slot }) {
  const [layout, setLayout] = useState(null);
  const [edit, setEdit] = useState(false);
  useEffect(() => { api.get(`/dashboards/${dashboard.id}/my-layout`).then((r) => setLayout(r.data)).catch(() => {}); }, [dashboard.id]);
  const widgets = useMemo(() => applyLayout(dashboard.widgets || [], layout), [dashboard, layout]);
  const current = () => ({ order: widgets.map((w) => w.id), widths: Object.fromEntries(widgets.map((w) => [w.id, w.width || "full"])) });
  const onMove = (from, to) => {
    const ids = widgets.map((w) => w.id);
    const [m] = ids.splice(ids.indexOf(from), 1);
    ids.splice(ids.indexOf(to), 0, m);
    setLayout({ ...current(), order: ids });
  };
  const onWidth = (id, w) => { const c = current(); setLayout({ ...c, widths: { ...c.widths, [id]: w } }); };
  const save = async () => {
    try { await api.put(`/dashboards/${dashboard.id}/my-layout`, current()); toast.success("Your layout is saved"); setEdit(false); } catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    try { await api.delete(`/dashboards/${dashboard.id}/my-layout`); setLayout(null); toast.success("Layout reset to default"); setEdit(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <DashboardRenderer dashboard={{ ...dashboard, widgets }} data={data} edit={edit} onMove={onMove} onWidth={onWidth} slot={slot} />
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {!edit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="customise-layout-btn"><LayoutGrid className="h-3.5 w-3.5 mr-1.5" />Arrange my dashboard</Button>}
        {edit && <>
          <span className="text-xs text-muted-foreground mr-2">Drag widgets to move them, choose a size on each widget, then save.</span>
          <Button size="sm" onClick={save} data-testid="save-layout-btn"><Check className="h-3.5 w-3.5 mr-1.5" />Save my layout</Button>
          <Button size="sm" variant="ghost" onClick={reset} data-testid="reset-layout-btn"><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset to default</Button>
          <Button size="sm" variant="ghost" onClick={() => { setEdit(false); api.get(`/dashboards/${dashboard.id}/my-layout`).then((r) => setLayout(r.data)); }} data-testid="cancel-layout-btn">Cancel</Button>
        </>}
      </div>
    </div>
  );
}
